import { useState, useRef } from 'react';
import { Modal, Button, Form, Alert, Table, Badge, ProgressBar } from 'react-bootstrap';
import type {
  CreateIngredientRequest,
  CreateIngredientRequestForCSV,
  IngredientCSVRow,
} from '../../../../models/Ingredient';

interface CSVUploadModalProps {
  readonly show: boolean;
  readonly onHide: () => void;
  readonly onUpload: (ingredients: CreateIngredientRequest[]) => void;
  readonly isUploading: boolean;
}

export const CSVUploadModal = ({ show, onHide, onUpload, isUploading }: CSVUploadModalProps) => {
  const [csvData, setCsvData] = useState<CreateIngredientRequestForCSV[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const [previewData, setPreviewData] = useState<CreateIngredientRequestForCSV[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parseCSVRow = (row: string[]): IngredientCSVRow | null => {
    if (row.length !== 7) {
      return null;
    }

    return {
      ingredient: row[0]?.trim() || '',
      risk: row[1]?.trim() || '',
      eng: row[2]?.trim() || '',
      kor: row[3]?.trim() || '',
      desc: row[4]?.trim() || '',
      efficacy: row[5]?.trim() || '',
      efficacy_desc: row[6]?.trim() || '',
    };
  };

  const validateIngredient = (ingredient: CreateIngredientRequestForCSV, index: number): string[] => {
    const errors: string[] = [];

    if (!ingredient.ingredientName) {
      errors.push(`Row ${index + 1}: Ingredient name is required`);
    }
    // risk is now optional for CSV upload
    // engName is now optional for CSV upload
    if (!ingredient.korName) {
      errors.push(`Row ${index + 1}: Korean name is required`);
    }
    // description is now optional for CSV upload
    if (!ingredient.efficacy) {
      errors.push(`Row ${index + 1}: Efficacy is required`);
    }
    if (!ingredient.efficacyDescription) {
      errors.push(`Row ${index + 1}: Efficacy description is required`);
    }

    return errors;
  };

  const convertCSVRowToIngredient = (csvRow: IngredientCSVRow): CreateIngredientRequestForCSV => {
    const result: CreateIngredientRequestForCSV = {
      ingredientName: csvRow.ingredient,
      korName: csvRow.kor,
      efficacy: csvRow.efficacy,
      efficacyDescription: csvRow.efficacy_desc,
    };

    // Only include risk if it has a value
    if (csvRow.risk && csvRow.risk.trim()) {
      result.risk = csvRow.risk.trim();
    }

    // Only include engName if it has a value
    if (csvRow.eng && csvRow.eng.trim()) {
      result.engName = csvRow.eng.trim();
    }

    // Only include description if it has a value
    if (csvRow.desc && csvRow.desc.trim()) {
      result.description = csvRow.desc.trim();
    }

    return result;
  };

  const parseCSV = (csvText: string): { data: CreateIngredientRequestForCSV[]; errors: string[] } => {
    const lines = csvText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line);
    const data: CreateIngredientRequestForCSV[] = [];
    const errors: string[] = [];

    if (lines.length === 0) {
      errors.push('CSV file is empty');
      return { data, errors };
    }

    // Skip header row (first line)
    const dataLines = lines.slice(1);

    dataLines.forEach((line, index) => {
      // Parse CSV row manually to handle commas within quoted strings
      const row = parseCSVLine(line);
      const csvRow = parseCSVRow(row);

      if (!csvRow) {
        errors.push(`Row ${index + 2}: Invalid CSV format (expected 7 columns)`);
        return;
      }

      const ingredient = convertCSVRowToIngredient(csvRow);
      const validationErrors = validateIngredient(ingredient, index + 1);

      if (validationErrors.length > 0) {
        errors.push(...validationErrors);
      } else {
        data.push(ingredient);
      }
    });

    return { data, errors };
  };

  // Simple CSV parser that handles quoted fields with commas
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current);
    return result.map((field) => field.replace(/^"|"$/g, '').trim());
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setErrors([]);
    setCsvData([]);
    setPreviewData([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvText = e.target?.result as string;
      const { data, errors } = parseCSV(csvText);

      setCsvData(data);
      setErrors(errors);
      setPreviewData(data.slice(0, 5)); // Show first 5 rows as preview
    };
    reader.readAsText(file);
  };

  const handleUpload = () => {
    if (csvData.length > 0) {
      // Convert CreateIngredientRequestForCSV to CreateIngredientRequest
      const ingredientsForAPI: CreateIngredientRequest[] = csvData.map((ingredient) => ({
        ...ingredient,
        risk: ingredient.risk || '1', // default risk level if not provided
        engName: ingredient.engName || ingredient.ingredientName, // default to ingredient name if not provided
        description: ingredient.description || 'No description provided', // default description if not provided
      }));
      onUpload(ingredientsForAPI);
    }
  };

  const handleClose = () => {
    setCsvData([]);
    setErrors([]);
    setPreviewData([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onHide();
  };

  const downloadTemplate = () => {
    const template = [
      'ingredient,risk,eng,kor,desc,efficacy,efficacy_desc',
      'Water;Aqua,1,Water;Aqua,정제수,"피부컨디셔닝제, 용제",Solvent,"Crystal-clear and universally essential..."',
      'Glycerin,1-2,Glycerin,글리세린,"변성제, 착향제, 헤어컨디셔닝제, 보습제",Humectant,"A humectant powerhouse..."',
    ].join('\n');

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ingredient_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Upload Ingredients from CSV</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <Button variant="outline-info" size="sm" onClick={downloadTemplate}>
            Download CSV Template
          </Button>
          <small className="text-muted ms-2">Download a template file to see the expected format</small>
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Select CSV File</Form.Label>
          <Form.Control
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={isUploading}
          />
          <Form.Text className="text-muted">
            Expected format: ingredient,risk,eng,kor,desc,efficacy,efficacy_desc
          </Form.Text>
        </Form.Group>

        {errors.length > 0 && (
          <Alert variant="danger">
            <Alert.Heading>Validation Errors</Alert.Heading>
            <ul className="mb-0">
              {errors.slice(0, 10).map((error, index) => (
                <li key={index}>{error}</li>
              ))}
              {errors.length > 10 && <li>... and {errors.length - 10} more errors</li>}
            </ul>
          </Alert>
        )}

        {csvData.length > 0 && errors.length === 0 && (
          <Alert variant="success">
            <Alert.Heading>Ready to Upload</Alert.Heading>
            <p className="mb-0">Found {csvData.length} valid ingredients ready for upload.</p>
          </Alert>
        )}

        {previewData.length > 0 && (
          <div className="mt-4">
            <h6>Preview (First 5 rows):</h6>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Ingredient Name</th>
                  <th>Risk</th>
                  <th>English Name</th>
                  <th>Korean Name</th>
                  <th>Description</th>
                  <th>Efficacy</th>
                </tr>
              </thead>
              <tbody>
                {previewData.map((ingredient, index) => (
                  <tr key={index}>
                    <td>{ingredient.ingredientName}</td>
                    <td>
                      <Badge bg="secondary">{ingredient.risk}</Badge>
                    </td>
                    <td>{ingredient.engName}</td>
                    <td>{ingredient.korName}</td>
                    <td style={{ maxWidth: '200px' }}>
                      {ingredient.description && ingredient.description.length > 50
                        ? `${ingredient.description.substring(0, 50)}...`
                        : ingredient.description || 'N/A'}
                    </td>
                    <td>{ingredient.efficacy}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {csvData.length > 5 && <small className="text-muted">... and {csvData.length - 5} more ingredients</small>}
          </div>
        )}

        {isUploading && (
          <div className="mt-3">
            <ProgressBar animated now={100} label="Uploading ingredients..." />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleUpload}
          disabled={csvData.length === 0 || errors.length > 0 || isUploading}
        >
          {isUploading ? 'Uploading...' : `Upload ${csvData.length} Ingredients`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
